import { Flex, Field, Select, createListCollection, Text, Portal } from '@chakra-ui/react';
import { Controller } from 'react-hook-form';
import { useCountries } from '@/scripts/hooks/useCountries';
import type { FieldProps } from './helpers';

interface EditLocationProps extends FieldProps {}

const EditLocation = ({ user, form }: EditLocationProps) => {
  const { t } = useTranslation();
  const {
    formState: { errors },
    control,
  } = form;
  const [value, setValue] = useState<string[]>([user?.location || '']);

  const { countries } = useCountries(user?.location || '');

  const collection = useMemo(() => {
    return createListCollection({
      items: countries,
    });
  }, [countries]);

  useEffect(() => {
    if (user?.location) {
      setValue([user.location]);
    }
  }, [user?.location]);

  return (
    <Flex
      w="full"
      px="3"
      py="2.5"
      bg="bg.panel"
      rounded="lg"
      alignItems="center"
      justifyContent="space-between"
    >
      <Text fontSize="sm">{t('profile.location')}</Text>
      <Field.Root
        invalid={!!errors.location}
        flex="1"
        flexDirection="row"
        justifyContent="flex-end"
      >
        <Controller
          control={control}
          name="location"
          render={({ field }) => (
            <Select.Root
              positioning={{ sameWidth: true, placement: 'bottom', strategy: 'fixed' }}
              collection={collection}
              size="sm"
              width="220px"
              name={field.name}
              value={value}
              onValueChange={({ value }) => {
                setValue(value);
                field.onChange(value);
              }}
            >
              <Select.HiddenSelect />
              <Select.Control>
                <Select.Trigger cursor="pointer" border="1px solid var(--colors-border-subtle)">
                  <Select.ValueText placeholder={t('profile.selectLocation')} />
                </Select.Trigger>
                <Select.IndicatorGroup>
                  <Select.Indicator />
                </Select.IndicatorGroup>
              </Select.Control>
              <Portal disabled>
                <Select.Positioner>
                  <Select.Content maxHeight="180px">
                    {collection.items.map((location) => (
                      <Select.Item item={location} key={location.value} cursor="pointer">
                        {location.label}
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Portal>
            </Select.Root>
          )}
        />
      </Field.Root>
    </Flex>
  );
};

const MemoizedEditLocation = memo(EditLocation);

export { MemoizedEditLocation as EditLocation };
